import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Palette, Save, Type } from "lucide-react";

interface BrandingSettingsProps {
  provider: {
    id: string;
    name: string;
    brand_primary_color?: string | null;
    brand_secondary_color?: string | null;
    brand_accent_color?: string | null;
    brand_font?: string | null;
    brand_heading_color?: string | null;
    brand_body_color?: string | null;
    brand_background_color?: string | null;
    brand_header_text_color?: string | null;
    brand_header_font_weight?: string | null;
    brand_header_icon_text_color?: string | null;
    brand_header_button_text_color?: string | null;
    brand_header_letter_spacing?: string | null;
  };
  onProviderUpdate: () => void;
}

const FONT_WEIGHT_OPTIONS = [
  { value: "300", label: "Light" },
  { value: "400", label: "Regular" },
  { value: "500", label: "Medium" },
  { value: "600", label: "Semibold" },
  { value: "700", label: "Bold" },
  { value: "800", label: "Extra Bold" },
];

const LETTER_SPACING_OPTIONS = [
  { value: "-0.05em", label: "Tight" },
  { value: "0", label: "Normal" },
  { value: "0.05em", label: "Wide" },
  { value: "0.1em", label: "Wider" },
  { value: "0.15em", label: "Widest" },
];

const FONT_OPTIONS = [
  { value: "DM Sans", label: "DM Sans", description: "Modern & clean" },
  { value: "Inter", label: "Inter", description: "Neutral & versatile" },
  { value: "Prompt", label: "Prompt", description: "Thai-inspired & friendly" },
  { value: "Questrial", label: "Questrial", description: "Geometric & minimal" },
  { value: "Outfit", label: "Outfit", description: "Wide & contemporary" },
  { value: "Lexend", label: "Lexend", description: "Clear & readable" },
  { value: "Montserrat", label: "Montserrat", description: "Classic & elegant" },
  { value: "Poppins", label: "Poppins", description: "Friendly & rounded" },
  { value: "Space Grotesk", label: "Space Grotesk", description: "Tech & bold" },
  { value: "Libre Franklin", label: "Libre Franklin", description: "Professional & versatile" },
  { value: "Work Sans", label: "Work Sans", description: "Simple & balanced" },
];

const PRESET_THEMES = [
  { 
    name: "Nordic Cycle", 
    primary: "#1a3a34", 
    secondary: "#2d5a54", 
    accent: "#4a7c74",
    font: "Questrial"
  },
  { 
    name: "Ocean Blue", 
    primary: "#1e3a5f", 
    secondary: "#2d5a8a", 
    accent: "#4a8bc4" 
  },
  { 
    name: "Sunset Warmth", 
    primary: "#8b4513", 
    secondary: "#cd853f", 
    accent: "#deb887" 
  },
  { 
    name: "Forest Green", 
    primary: "#2d4a2d", 
    secondary: "#4a7a4a", 
    accent: "#6b9b6b" 
  },
  { 
    name: "Purple Zen", 
    primary: "#4a2d5a", 
    secondary: "#6b4a7a", 
    accent: "#8b6b9b" 
  },
  { 
    name: "Charcoal", 
    primary: "#2a2a2a", 
    secondary: "#4a4a4a", 
    accent: "#6a6a6a" 
  },
];

const BrandingSettings = ({ provider, onProviderUpdate }: BrandingSettingsProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [primaryColor, setPrimaryColor] = useState(provider.brand_primary_color || "");
  const [secondaryColor, setSecondaryColor] = useState(provider.brand_secondary_color || "");
  const [accentColor, setAccentColor] = useState(provider.brand_accent_color || "");
  const [brandFont, setBrandFont] = useState(provider.brand_font || "");
  const [headingColor, setHeadingColor] = useState(provider.brand_heading_color || "");
  const [bodyColor, setBodyColor] = useState(provider.brand_body_color || "");
  const [backgroundColor, setBackgroundColor] = useState(provider.brand_background_color || "");
  const [headerTextColor, setHeaderTextColor] = useState(provider.brand_header_text_color || "");
  const [headerFontWeight, setHeaderFontWeight] = useState(provider.brand_header_font_weight || "");
  const [headerIconTextColor, setHeaderIconTextColor] = useState(provider.brand_header_icon_text_color || "");
  const [headerButtonTextColor, setHeaderButtonTextColor] = useState(provider.brand_header_button_text_color || "");
  const [headerLetterSpacing, setHeaderLetterSpacing] = useState(provider.brand_header_letter_spacing || "");
  const { toast } = useToast();

  const applyPreset = (preset: typeof PRESET_THEMES[0]) => {
    setPrimaryColor(preset.primary);
    setSecondaryColor(preset.secondary);
    setAccentColor(preset.accent);
    if (preset.font) {
      setBrandFont(preset.font);
    }
  };

  const clearColors = () => {
    setPrimaryColor("");
    setSecondaryColor("");
    setAccentColor("");
    setBrandFont("");
    setHeadingColor("");
    setBodyColor("");
    setBackgroundColor("");
    setHeaderTextColor("");
    setHeaderFontWeight("");
    setHeaderIconTextColor("");
    setHeaderButtonTextColor("");
    setHeaderLetterSpacing("");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const { error } = await supabase
        .from('providers')
        .update({
          brand_primary_color: primaryColor || null,
          brand_secondary_color: secondaryColor || null,
          brand_accent_color: accentColor || null,
          brand_font: brandFont || null,
          brand_heading_color: headingColor || null,
          brand_body_color: bodyColor || null,
          brand_background_color: backgroundColor || null,
          brand_header_text_color: headerTextColor || null,
          brand_header_font_weight: headerFontWeight || null,
          brand_header_icon_text_color: headerIconTextColor || null,
          brand_header_button_text_color: headerButtonTextColor || null,
          brand_header_letter_spacing: headerLetterSpacing || null,
          updated_at: new Date().toISOString()
        })
        .eq('id', provider.id);

      if (error) throw error;

      toast({
        title: "Branding Updated",
        description: "Your brand colors have been saved.",
      });
      
      onProviderUpdate();
    } catch (error) {
      console.error('Error updating branding:', error);
      toast({
        title: "Error",
        description: "Failed to update branding. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Palette className="h-5 w-5 text-purple-600" />
          <CardTitle className="text-lg sm:text-xl">Brand Settings</CardTitle>
        </div>
        <p className="text-sm text-muted-foreground mt-1">
          Customize the look of your booking page with your brand colors and font.
        </p>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Preset Themes */}
          <div className="space-y-3">
            <Label>Quick Presets</Label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {PRESET_THEMES.map((preset) => (
                <button
                  key={preset.name}
                  type="button"
                  onClick={() => applyPreset(preset)}
                  className="flex items-center gap-2 p-2 rounded-lg border border-border hover:bg-muted transition-colors text-left"
                >
                  <div className="flex gap-0.5">
                    <div 
                      className="w-4 h-4 rounded-full" 
                      style={{ backgroundColor: preset.primary }}
                    />
                    <div 
                      className="w-4 h-4 rounded-full" 
                      style={{ backgroundColor: preset.secondary }}
                    />
                    <div 
                      className="w-4 h-4 rounded-full" 
                      style={{ backgroundColor: preset.accent }}
                    />
                  </div>
                  <span className="text-xs font-medium truncate">{preset.name}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Custom Colors */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="primaryColor">Primary Color</Label>
              <div className="flex gap-2">
                <Input
                  id="primaryColor"
                  type="color"
                  value={primaryColor || "#000000"}
                  onChange={(e) => setPrimaryColor(e.target.value)}
                  className="w-12 h-10 p-1 cursor-pointer"
                />
                <Input
                  type="text"
                  value={primaryColor}
                  onChange={(e) => setPrimaryColor(e.target.value)}
                  placeholder="#000000"
                  className="flex-1 font-mono text-sm"
                />
              </div>
              <p className="text-xs text-muted-foreground">Header & buttons</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="secondaryColor">Secondary Color</Label>
              <div className="flex gap-2">
                <Input
                  id="secondaryColor"
                  type="color"
                  value={secondaryColor || "#333333"}
                  onChange={(e) => setSecondaryColor(e.target.value)}
                  className="w-12 h-10 p-1 cursor-pointer"
                />
                <Input
                  type="text"
                  value={secondaryColor}
                  onChange={(e) => setSecondaryColor(e.target.value)}
                  placeholder="#333333"
                  className="flex-1 font-mono text-sm"
                />
              </div>
              <p className="text-xs text-muted-foreground">Accents & hover states</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="accentColor">Accent Color</Label>
              <div className="flex gap-2">
                <Input
                  id="accentColor"
                  type="color"
                  value={accentColor || "#666666"}
                  onChange={(e) => setAccentColor(e.target.value)}
                  className="w-12 h-10 p-1 cursor-pointer"
                />
                <Input
                  type="text"
                  value={accentColor}
                  onChange={(e) => setAccentColor(e.target.value)}
                  placeholder="#666666"
                  className="flex-1 font-mono text-sm"
                />
              </div>
              <p className="text-xs text-muted-foreground">Highlights & links</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="headerTextColor">Header Text Color</Label>
              <div className="flex gap-2">
                <Input
                  id="headerTextColor"
                  type="color"
                  value={headerTextColor || "#ffffff"}
                  onChange={(e) => setHeaderTextColor(e.target.value)}
                  className="w-12 h-10 p-1 cursor-pointer"
                />
                <Input
                  type="text"
                  value={headerTextColor}
                  onChange={(e) => setHeaderTextColor(e.target.value)}
                  placeholder="#ffffff"
                  className="flex-1 font-mono text-sm"
                />
              </div>
              <p className="text-xs text-muted-foreground">Provider name color in header (when primary color is set)</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="headerFontWeight">Header Name Font Weight</Label>
              <Select value={headerFontWeight} onValueChange={setHeaderFontWeight}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Default (Semibold)" />
                </SelectTrigger>
                <SelectContent>
                  {FONT_WEIGHT_OPTIONS.map((weight) => (
                    <SelectItem key={weight.value} value={weight.value}>
                      <span style={{ fontWeight: weight.value }}>{weight.label}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">Font weight for provider name in header</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="headerLetterSpacing">Header Name Letter Spacing</Label>
              <Select value={headerLetterSpacing} onValueChange={setHeaderLetterSpacing}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Default (Normal)" />
                </SelectTrigger>
                <SelectContent>
                  {LETTER_SPACING_OPTIONS.map((spacing) => (
                    <SelectItem key={spacing.value} value={spacing.value}>
                      <span style={{ letterSpacing: spacing.value }}>{spacing.label}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">Letter spacing for provider name in header</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="headerIconTextColor">Profile Icon Text Color</Label>
              <div className="flex gap-2">
                <Input
                  id="headerIconTextColor"
                  type="color"
                  value={headerIconTextColor || "#ffffff"}
                  onChange={(e) => setHeaderIconTextColor(e.target.value)}
                  className="w-12 h-10 p-1 cursor-pointer"
                />
                <Input
                  type="text"
                  value={headerIconTextColor}
                  onChange={(e) => setHeaderIconTextColor(e.target.value)}
                  placeholder="#ffffff"
                  className="flex-1 font-mono text-sm"
                />
              </div>
              <p className="text-xs text-muted-foreground">User profile icon initials color in header</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="headerButtonTextColor">Book Now Button Text Color</Label>
              <div className="flex gap-2">
                <Input
                  id="headerButtonTextColor"
                  type="color"
                  value={headerButtonTextColor || "#000000"}
                  onChange={(e) => setHeaderButtonTextColor(e.target.value)}
                  className="w-12 h-10 p-1 cursor-pointer"
                />
                <Input
                  type="text"
                  value={headerButtonTextColor}
                  onChange={(e) => setHeaderButtonTextColor(e.target.value)}
                  placeholder="#000000"
                  className="flex-1 font-mono text-sm"
                />
              </div>
              <p className="text-xs text-muted-foreground">Text color for Book Now / Sign up button in header</p>
            </div>
          </div>

          {/* Font Selection */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Type className="h-4 w-4 text-muted-foreground" />
              <Label>Brand Font</Label>
            </div>
            <Select value={brandFont} onValueChange={setBrandFont}>
              <SelectTrigger className="w-full sm:w-64">
                <SelectValue placeholder="Select a font (default: DM Sans)" />
              </SelectTrigger>
              <SelectContent>
                {FONT_OPTIONS.map((font) => (
                  <SelectItem key={font.value} value={font.value}>
                    <span style={{ fontFamily: font.value }}>{font.label}</span>
                    <span className="text-muted-foreground ml-2 text-xs">- {font.description}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">Applied to headings and key text on your booking page</p>
          </div>

          {/* Text Colors */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="headingColor">Heading Text Color</Label>
              <div className="flex gap-2">
                <Input
                  id="headingColor"
                  type="color"
                  value={headingColor || "#1a1a1a"}
                  onChange={(e) => setHeadingColor(e.target.value)}
                  className="w-12 h-10 p-1 cursor-pointer"
                />
                <Input
                  type="text"
                  value={headingColor}
                  onChange={(e) => setHeadingColor(e.target.value)}
                  placeholder="#1a1a1a"
                  className="flex-1 font-mono text-sm"
                />
              </div>
              <p className="text-xs text-muted-foreground">Titles & headings</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="bodyColor">Body Text Color</Label>
              <div className="flex gap-2">
                <Input
                  id="bodyColor"
                  type="color"
                  value={bodyColor || "#4a4a4a"}
                  onChange={(e) => setBodyColor(e.target.value)}
                  className="w-12 h-10 p-1 cursor-pointer"
                />
                <Input
                  type="text"
                  value={bodyColor}
                  onChange={(e) => setBodyColor(e.target.value)}
                  placeholder="#4a4a4a"
                  className="flex-1 font-mono text-sm"
                />
              </div>
              <p className="text-xs text-muted-foreground">Paragraphs & descriptions</p>
            </div>
          </div>

          {/* Background Color */}
          <div className="space-y-2">
            <Label htmlFor="backgroundColor">Page Background Color</Label>
            <div className="flex gap-2">
              <Input
                id="backgroundColor"
                type="color"
                value={backgroundColor || "#ffffff"}
                onChange={(e) => setBackgroundColor(e.target.value)}
                className="w-12 h-10 p-1 cursor-pointer"
              />
              <Input
                type="text"
                value={backgroundColor}
                onChange={(e) => setBackgroundColor(e.target.value)}
                placeholder="#ffffff"
                className="flex-1 sm:w-48 font-mono text-sm"
              />
            </div>
            <p className="text-xs text-muted-foreground">Background color for your booking page</p>
          </div>

          {/* Preview */}
          {primaryColor && (
            <div className="space-y-2">
              <Label>Preview</Label>
              <div 
                className="rounded-xl p-4 border border-border"
                style={{ backgroundColor: primaryColor, fontFamily: brandFont || 'DM Sans' }}
              >
                <div className="flex items-center justify-between">
                  <span style={{ 
                    color: headerTextColor || '#ffffff', 
                    fontWeight: headerFontWeight || '600',
                    letterSpacing: headerLetterSpacing || undefined
                  }}>{provider.name}</span>
                  <button 
                    type="button"
                    className="px-4 py-2 rounded-full text-sm font-medium"
                    style={{ 
                      backgroundColor: secondaryColor || primaryColor,
                      color: headerButtonTextColor || '#ffffff'
                    }}
                  >
                    Book Now
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3">
            <Button type="submit" disabled={isLoading}>
              <Save className="h-4 w-4 mr-2" />
              {isLoading ? "Saving..." : "Save Branding"}
            </Button>
            <Button type="button" variant="outline" onClick={clearColors}>
              Reset to Default
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

export default BrandingSettings;
